import React from 'react';
import { a11y } from '../utils/a11yLabels';

/**
 * The one palette.
 *
 * It lived inside SettingsPage, which was fine while labels were the only
 * thing with a colour. Now the people in a shared calendar have one too, and
 * two palettes would have drifted apart within a week — Silvia would have had
 * 39 colours for a label and eight for a person, and no way to make Mirko the
 * same blue as the label she thinks of as his.
 */

export const PRESET_COLORS = [
  // Six loud ones added at Silvia's request, each beside its own family. They
  // are the colour of the label TEXT on a card, not a filled chip, so a true
  // highlighter yellow would be a word you cannot read on a cream background —
  // the numbers are in the note below. They are as loud as the existing
  // palette already allows, and every one is at least 12 perceptual units from
  // the two it sits next to, so no dot repeats its neighbour.
  //
  // Thirteen pastels, added at Silvia's request — "tutti sul pastello ma non
  // troppo chiari" — each one placed beside the family it belongs to, marked
  // ·p· below.
  //
  // "Not too pale" is the whole difficulty and she named it herself. This
  // colour is used two ways: as the WORD of the label, written on the card,
  // and as the 25% wash behind an event. A wash is forgiving; a word is not. A
  // true pastel yellow written on a cream card has a contrast of about 1.5 —
  // that is not "faint", that is gone, and it is exactly how the first theme
  // yellow disappeared. So each of the thirteen was pushed down until it
  // reached at least 2.05 against a white card, which is the point where a
  // bold word at this size stays readable while the colour still reads as a
  // pastel. On the dark theme they are all above 5.5, comfortably.
  //
  // The price is the yellow: a pastel yellow that can be read on white is an
  // ochre. There is no third option — that is what the arithmetic of yellow on
  // white allows. Every one of the 39 is also at least 11 perceptual units
  // from every other, so no two dots in the picker look like the same dot.
  '#ffbf00', '#ffec1a', '#eaff00', '#c6af60',   // yellow + fluo + ·p·
  '#fb5f04', '#ff8000', '#ffa100', '#e9a56c',   // orange + fluo + ·p·
  '#ff1500', '#d51710', '#ef8b7e',              // red + ·p· corallo
  '#ee2f88', '#bc2f63', '#ff00c8', '#e79ab5', '#d98cc4',  // pink + fluo + ·p·×2
  '#bf8cd9', '#a14dcb', '#b026ff', '#9d93cf',   // violet + fluo + ·p·
  '#096ee1', '#245389', '#6f98e2', '#7fa9d9',   // blue + ·p·×2
  '#0db5f2', '#21708c', '#00e5ff', '#81bbcf',   // light blue + fluo + ·p·
  '#389f42', '#447449', '#2fff6a', '#7bc1b2', '#90c186',  // green + fluo + ·p· acqua + ·p·
  '#9d5125', '#61412e', '#c0a184',              // brown + ·p· sabbia
  '#464d59', '#242a32', '#a9b0bb',              // grey + ·p·
];
// Shown as one wrapping row in the order above, so the three shades of a colour
// stay together and the colours themselves follow the wheel. Previously they
// were split into "all the deep ones, then all the loud ones", which put a
// colour's own shades at opposite ends of the picker.
const COLORS_BASE = PRESET_COLORS;
const COLORS_MORE = [];

export default function ColorPicker({ value, onChange, lang }) {
  // Each swatch is an empty circle: its colour IS its whole meaning, and a
  // colour cannot be spoken. Numbered instead, with aria-pressed so a screen
  // reader says which one is currently chosen.
  const A = a11y(lang);
  // Show ALL colours at once (no "+N" toggle).
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {[...COLORS_BASE, ...COLORS_MORE].map((c, i) => (
        <button key={c} type="button" onClick={() => onChange(c)}
          aria-label={`${A.color} ${i + 1}`} aria-pressed={value === c}
          className="tap-grow" style={{ '--tap-x': '3px', width: 28, height: 28, borderRadius: '50%', background: c, border: value === c ? '3px solid var(--color-text)' : '2px solid transparent', cursor: 'pointer', flexShrink: 0 }} />
      ))}
    </div>
  );
}
